import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useWA, Card, Btn, Field, Toggle, ImageInput, StatusBadge, Skeleton, ErrorBox, Badge, num, fdt, strip } from "../ui";
import RichEditor from "../RichEditor";
import { siteLink } from "../api";

const EMPTY = { title: "", slug: "", content: "", shortDescription: "", coverImage: "", coverAlt: "", seoTitle: "", seoDescription: "", seoKeywords: "", focusKeyword: "", isPillar: false, language: "hi", status: "DRAFT", categoryId: "", tags: [], publishedAt: "", monetization: "NONE", monetNote: "", authorEmail: "", views: 0 };

const toLocal = (d) => {
  if (!d) return "";
  const x = new Date(d);
  return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const slugify = (s) => s.toLowerCase().trim().replace(/[\s_]+/g, "-").replace(/[^\p{L}\p{N}\p{M}-]/gu, "").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

function Counter({ n, min, max }) {
  const ok = n >= min && n <= max;
  return <span className={ok ? "text-emerald-600 font-semibold" : n > max ? "text-rose-600 font-semibold" : ""}>{n}/{max}</span>;
}

export default function BlogEditor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const { api, run, toast, confirm } = useWA();
  const [f, setF] = useState(EMPTY);
  const [meta, setMeta] = useState(null);
  const [cats, setCats] = useState([]);
  const [tagText, setTagText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const set = (k, v) => {
    setF((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (isNew) {
        const c = await api("categories");
        setCats(c.items);
        setF(EMPTY);
        setMeta(null);
      } else {
        const r = await api("blogGet", { id });
        const b = r.blog;
        setCats(r.categories);
        setF({
          ...EMPTY,
          ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, b[k] === null || b[k] === undefined ? EMPTY[k] : b[k]])),
          categoryId: b.categoryId || "",
          publishedAt: toLocal(b.publishedAt),
          authorEmail: "",
        });
        setMeta({ author: b.author, likes: b.likes, comments: b.comments, createdAt: b.createdAt, updatedAt: b.updatedAt, slug: b.slug, status: b.status, readSeconds: b.readSeconds });
      }
      setDirty(false);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [api, id, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const save = useCallback(async (statusOverride) => {
    const body = { ...f, id: isNew ? undefined : id, status: statusOverride || f.status, publishedAt: f.publishedAt ? new Date(f.publishedAt).toISOString() : null };
    if (!body.title.trim()) return toast("Title लिखें", "error");
    setSaving(true);
    try {
      const r = await run("blogSave", body, "Blog save हो गया");
      setDirty(false);
      if (isNew) nav(`/website/blogs/${r.id}`, { replace: true });
      else load();
    } catch (e) {
      /* toast shown */
    }
    setSaving(false);
  }, [f, id, isNew, run, toast, nav, load]);

  useEffect(() => {
    const k = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [save]);

  const del = async () => {
    if (!(await confirm({ title: "यह blog हमेशा के लिए delete करें?", text: "Likes, comments और views भी हट जाएँगे।", danger: true }))) return;
    try {
      await run("blogDelete", { ids: [id] }, "Blog delete हो गया");
      setDirty(false);
      nav("/website/blogs");
    } catch (e) {
      /* toast */
    }
  };

  const addTag = (t) => {
    const v = t.trim().replace(/,$/, "");
    if (v && !f.tags.includes(v) && f.tags.length < 15) set("tags", [...f.tags, v]);
    setTagText("");
  };

  if (loading) return <Skeleton rows={10} h={56} />;
  if (error) return <ErrorBox msg={error} retry={load} />;

  const words = strip(f.content).split(" ").filter(Boolean).length;
  const seoT = f.seoTitle || f.title;
  const seoD = f.seoDescription || f.shortDescription;
  const checks = [
    [f.title.length >= 20 && f.title.length <= 70, "Title 20–70 अक्षर"],
    [words >= 300, `Content कम से कम 300 शब्द (अभी ${num(words)})`],
    [!!f.coverImage, "Cover image"],
    [!!f.shortDescription, "Short description"],
    [!!f.categoryId, "Category"],
    [f.tags.length > 0, "Tags"],
    [f.seoDescription.length >= 70 && f.seoDescription.length <= 160, "SEO description 70–160"],
    [!!f.focusKeyword && (f.title + " " + strip(f.content)).toLowerCase().includes(f.focusKeyword.toLowerCase()), "Focus keyword title/content में"],
  ];
  const score = Math.round((checks.filter((c) => c[0]).length / checks.length) * 100);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Link to="/website/blogs" className="w-10 h-10 rounded-xl grid place-items-center border wa-line hover:bg-slate-500/10" aria-label="Back"><i className="fa-solid fa-arrow-left" /></Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-extrabold truncate">{isNew ? "नया Blog" : f.title || "Blog edit"}</h1>
          <p className="text-xs wa-muted">{meta ? <>{meta.author && (meta.author.name || meta.author.email)} · बनाया {fdt(meta.createdAt)} · बदला {fdt(meta.updatedAt)}</> : "सारे fields भरें, फिर Save करें"} {dirty && <Badge tone="amber" className="ml-1">Save नहीं हुआ</Badge>}</p>
        </div>
        {meta && <a href={siteLink(`/blogs/${encodeURIComponent(meta.slug)}`)} target="_blank" rel="noreferrer"><Btn icon="fa-solid fa-arrow-up-right-from-square">Site पर देखें</Btn></a>}
        <Btn variant="primary" icon="fa-solid fa-floppy-disk" loading={saving} onClick={() => save()}>Save <span className="opacity-60 text-[11px] hidden sm:inline">Ctrl+S</span></Btn>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 items-start">
        <div className="space-y-5 min-w-0">
          <Card>
            <input className="w-full bg-transparent text-2xl md:text-[28px] font-extrabold outline-none placeholder:opacity-40" placeholder="Blog का title…" value={f.title} onChange={(e) => set("title", e.target.value)} aria-label="Title" />
            <div className="flex items-center gap-2 mt-3 text-[13px]">
              <span className="wa-muted shrink-0">ycnp.in/blogs/</span>
              <input className="wa-input !h-9" value={f.slug} placeholder={slugify(f.title) || "blog-ka-address"} onChange={(e) => set("slug", e.target.value)} aria-label="Slug" />
              <Btn size="sm" variant="ghost" onClick={() => set("slug", slugify(f.title))} title="Title से बनाएँ"><i className="fa-solid fa-wand-magic-sparkles" /></Btn>
            </div>
          </Card>
          <RichEditor value={f.content} onChange={(v) => set("content", v)} placeholder="Blog लिखना शुरू करें…" />
          <Card title="Short description" subtitle="Blog list और Google में दिखता है">
            <textarea rows={3} className="wa-input" value={f.shortDescription} maxLength={300} onChange={(e) => set("shortDescription", e.target.value)} placeholder="2-3 लाइन में blog किस बारे में है…" />
            <p className="text-[11px] wa-muted text-right mt-1">{f.shortDescription.length}/300</p>
          </Card>
          <Card title="SEO — Google में कैसा दिखेगा" icon="fa-brands fa-google">
            <div className="rounded-2xl border wa-line p-4 mb-4" style={{ background: "color-mix(in srgb, var(--wa-ink) 3%, transparent)" }}>
              <p className="text-[12px] text-emerald-700 dark:text-emerald-400 truncate">ycnp.in › blogs › {f.slug || slugify(f.title)}</p>
              <p className="text-[18px] text-blue-700 dark:text-blue-400 leading-snug line-clamp-1">{seoT || "Title"}</p>
              <p className="text-[13px] wa-muted line-clamp-2">{seoD || "Description यहाँ दिखेगा…"}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SEO title" right={<Counter n={f.seoTitle.length} min={30} max={70} />} hint="खाली छोड़ें तो blog title लगेगा">
                <input className="wa-input" value={f.seoTitle} maxLength={120} onChange={(e) => set("seoTitle", e.target.value)} />
              </Field>
              <Field label="Focus keyword" hint="मुख्य शब्द जिससे लोग खोजेंगे">
                <input className="wa-input" value={f.focusKeyword} onChange={(e) => set("focusKeyword", e.target.value)} />
              </Field>
              <Field label="SEO description" right={<Counter n={f.seoDescription.length} min={70} max={160} />} className="md:col-span-2">
                <textarea rows={2} className="wa-input" value={f.seoDescription} maxLength={200} onChange={(e) => set("seoDescription", e.target.value)} />
              </Field>
              <Field label="SEO keywords" hint="Comma से अलग करें" className="md:col-span-2">
                <input className="wa-input" value={f.seoKeywords} onChange={(e) => set("seoKeywords", e.target.value)} />
              </Field>
            </div>
          </Card>
        </div>

        <div className="space-y-5 xl:sticky xl:top-20">
          <Card title="Publish" icon="fa-solid fa-paper-plane">
            {meta && <div className="flex items-center gap-2 mb-3 text-sm">अभी: <StatusBadge s={meta.status} /></div>}
            <Field label="Status">
              <select className="wa-input" value={f.status} onChange={(e) => set("status", e.target.value)}>
                <option value="DRAFT">Draft (छिपा हुआ)</option>
                <option value="PUBLISHED">Published (सबको दिखेगा)</option>
                <option value="SCHEDULED">Scheduled (तय समय पर)</option>
                <option value="UNLISTED">Unlisted (सिर्फ link से)</option>
                <option value="PRIVATE">Private</option>
              </select>
            </Field>
            {(f.status === "SCHEDULED" || f.status === "PUBLISHED") && (
              <Field label={f.status === "SCHEDULED" ? "कब publish हो" : "Publish date"} className="mt-3">
                <input type="datetime-local" className="wa-input" value={f.publishedAt} onChange={(e) => set("publishedAt", e.target.value)} />
              </Field>
            )}
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Btn variant="ghost" onClick={() => save("DRAFT")} loading={saving}>Draft save</Btn>
              <Btn variant="success" icon="fa-solid fa-globe" onClick={() => save(f.status === "SCHEDULED" ? "SCHEDULED" : "PUBLISHED")} loading={saving}>{f.status === "SCHEDULED" ? "Schedule" : "Publish"}</Btn>
            </div>
            {!isNew && <Btn variant="ghost" className="w-full mt-2 !text-rose-600" icon="fa-regular fa-trash-can" onClick={del}>Delete blog</Btn>}
          </Card>

          <Card title={`SEO score: ${score}%`} icon="fa-solid fa-gauge">
            <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: "color-mix(in srgb, var(--wa-ink) 8%, transparent)" }}>
              <div className="h-full rounded-full" style={{ width: score + "%", background: score >= 80 ? "#16a34a" : score >= 50 ? "#f59e0b" : "#ef4444", transition: "width .4s" }} />
            </div>
            <ul className="space-y-1.5">
              {checks.map(([ok, l]) => (
                <li key={l} className="flex items-center gap-2 text-[12.5px]"><i className={ok ? "fa-solid fa-circle-check text-emerald-500" : "fa-regular fa-circle wa-muted"} /><span className={ok ? "" : "wa-muted"}>{l}</span></li>
              ))}
            </ul>
          </Card>

          <Card title="Category और Tags" icon="fa-solid fa-tags">
            <Field label="Category">
              <select className="wa-input" value={f.categoryId} onChange={(e) => set("categoryId", e.target.value)}>
                <option value="">— चुनें —</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Tags" hint="लिखकर Enter दबाएँ" className="mt-3">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {f.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full text-[12px] font-semibold" style={{ background: "color-mix(in srgb, var(--wa-accent) 14%, transparent)", color: "var(--wa-accent)" }}>
                    #{t}
                    <button type="button" onClick={() => set("tags", f.tags.filter((x) => x !== t))} className="w-5 h-5 rounded-full hover:bg-black/10" aria-label={`Remove ${t}`}><i className="fa-solid fa-xmark text-[10px]" /></button>
                  </span>
                ))}
              </div>
              <input className="wa-input" value={tagText} onChange={(e) => (e.target.value.endsWith(",") ? addTag(e.target.value) : setTagText(e.target.value))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagText); } }} placeholder="tag लिखें…" />
            </Field>
          </Card>

          <Card title="Cover image" icon="fa-regular fa-image">
            <ImageInput label="" value={f.coverImage} folder="blogs" onChange={(v) => set("coverImage", v)} />
            <Field label="Image alt text" hint="Google और नेत्रहीन पाठकों के लिए" className="mt-3">
              <input className="wa-input" value={f.coverAlt} onChange={(e) => set("coverAlt", e.target.value)} />
            </Field>
          </Card>

          <Card title="Monetization" icon="fa-solid fa-sack-dollar">
            <select className="wa-input" value={f.monetization} onChange={(e) => set("monetization", e.target.value)}>
              <option value="NONE">None</option>
              <option value="REQUESTED">Requested (author ने माँगा)</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <Field label="Note (author को दिखेगा)" className="mt-3">
              <input className="wa-input" value={f.monetNote} onChange={(e) => set("monetNote", e.target.value)} />
            </Field>
          </Card>

          <Card title="और options" icon="fa-solid fa-sliders">
            <Toggle label="Pillar post" desc="ज़रूरी / मुख्य लेख के रूप में दिखेगा" checked={f.isPillar} onChange={(v) => set("isPillar", v)} />
            <Field label="भाषा" className="mt-1">
              <select className="wa-input" value={f.language} onChange={(e) => set("language", e.target.value)}>
                <option value="hi">हिंदी</option>
                <option value="en">English</option>
                <option value="hinglish">Hinglish</option>
              </select>
            </Field>
            <Field label={isNew ? "Author का email *" : "Author बदलें (email)"} hint={isNew ? "वेबसाइट पर इस email का account होना चाहिए" : `अभी: ${meta && meta.author ? meta.author.email : "—"}`} className="mt-3">
              <input className="wa-input" type="email" value={f.authorEmail} onChange={(e) => set("authorEmail", e.target.value)} placeholder="writer@email.com" />
            </Field>
            {!isNew && (
              <Field label="Views (ठीक करना हो तो)" className="mt-3">
                <input className="wa-input" type="number" min="0" value={f.views} onChange={(e) => set("views", e.target.value)} />
              </Field>
            )}
            {meta && <p className="text-[12px] wa-muted mt-3">♥ {num(meta.likes)} likes · 💬 {num(meta.comments)} comments · <Link className="underline" to={`/website/comments?blog=${id}`}>comments देखें</Link></p>}
          </Card>
        </div>
      </div>
    </div>
  );
}
